import {
  normalizeUserState,
  normalizeUserStateMutation,
  type UserState,
  type UserStateMutation,
} from "./user-state";
import type { ActivityBatch } from "./activity";
import {
  normalizePriceWatchState,
  type PriceWatchState,
} from "./price-watch-model";

export const USER_STATE_PENDING_KEY = "kiloprice.user-state.pending.v1";
export const USER_STATE_BOOTSTRAP_KEY = "kiloprice.user-state.bootstrap.v1";

/** Browser-local cooldown only. Pending edits remain in the existing local queue. */
export class UserStateRetryWindow {
  private until = 0;

  remaining(now = Date.now()): number { return Math.max(0, this.until - now); }

  observe(response: Response, now = Date.now()): void {
    if (response.ok) { this.until = 0; return; }
    if (response.status !== 503 && response.status !== 429) return;
    const seconds = Number(response.headers.get("Retry-After"));
    if (Number.isFinite(seconds) && seconds > 0) {
      this.until = now + Math.min(seconds, 300) * 1_000;
    }
  }
}

const retryWindow = new UserStateRetryWindow();

export interface UserStateSyncResult {
  userId: string;
  revision: number;
  updatedAt: string;
  state: UserState;
  isTester: boolean;
  priceWatches?: PriceWatchState;
}

export class UserStateSyncError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly reason: string | null = null,
  ) {
    super(code);
    this.name = "UserStateSyncError";
  }
}

export function isTerminalUserStateSyncError(error: unknown): boolean {
  if (!(error instanceof UserStateSyncError)) {
    return false;
  }
  return (
    error.code === "unauthorized" ||
    error.code === "init_data_required" ||
    (error.status >= 400 &&
      error.status < 500 &&
      error.status !== 408 &&
      error.status !== 429)
  );
}

export function userStateSyncRetryDelay(failureCount: number): number {
  if (failureCount <= 0) {
    return 500;
  }
  return Math.min(30_000, 500 * 2 ** Math.min(failureCount, 6));
}

export async function syncUserState(
  initData: string,
  options: {
    bootstrapState?: UserState;
    mutation?: UserStateMutation;
    activity?: ActivityBatch;
  } = {},
): Promise<UserStateSyncResult> {
  if (retryWindow.remaining() > 0) {
    // Throw, do not synthesize success or an empty server state. The caller
    // restores its mutation/activity and keeps the user's local list intact.
    throw new UserStateSyncError("storage_unavailable", 503);
  }
  const response = await fetch("/api/user-state/sync", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      initData,
      bootstrapState: options.bootstrapState,
      mutation: options.mutation,
      activity: options.activity,
    }),
  });
  retryWindow.observe(response);
  let body: Record<string, unknown>;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    throw new UserStateSyncError("invalid_response", response.status);
  }
  const state = normalizeUserState(body.state);
  const priceWatches =
    body.priceWatches === undefined
      ? undefined
      : normalizePriceWatchState(body.priceWatches);
  if (
    !response.ok ||
    body.ok !== true ||
    typeof body.userId !== "string" ||
    typeof body.revision !== "number" ||
    typeof body.updatedAt !== "string" ||
    !state ||
    (body.priceWatches !== undefined && !priceWatches)
  ) {
    throw new UserStateSyncError(
      typeof body.error === "string" ? body.error : "user_state_sync_failed",
      response.status,
      typeof body.reason === "string" ? body.reason : null,
    );
  }
  return {
    userId: body.userId,
    revision: body.revision,
    updatedAt: body.updatedAt,
    state,
    isTester: body.isTester === true,
    ...(priceWatches ? { priceWatches } : {}),
  };
}

export function parsePendingUserStateMutation(
  value: string | null,
): UserStateMutation | null {
  if (!value) {
    return null;
  }
  try {
    return normalizeUserStateMutation(JSON.parse(value));
  } catch {
    return null;
  }
}
